import { useEffect, useState } from "react";
import { Eye, EyeOff, X, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = "nc_restore_prompt_dismissed";

/**
 * 회원정보보기 목록에서 숨김 처리된 회원이 로그인한 경우,
 * "다시 노출하시겠습니까?" 모달을 띄워 선택 권한을 부여합니다.
 *
 * - 노출하기: POST /api/auth/restore-listing → 즉시 hidden_from_listing=false
 * - 유지하기: 세션 동안 다시 보지 않음
 */
export default function RestoreListingPrompt() {
  const { user, token, updateUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !token) return;
    if (!user.hidden_from_listing) return;
    // 이번 세션에서 이미 닫았으면 다시 띄우지 않음
    try {
      if (sessionStorage.getItem(SESSION_KEY) === "1") return;
    } catch (e) {}
    setOpen(true);
  }, [user, token]);

  const handleRestore = async () => {
    if (busy || !token) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/restore-listing`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        // user 상태에서 hidden_from_listing 해제
        updateUser({ ...user, hidden_from_listing: false });
        toast.success("회원정보보기 목록에 다시 노출되었습니다!");
        setOpen(false);
      } else {
        toast.error("처리에 실패했습니다. 잠시 후 다시 시도해주세요.");
      }
    } catch (e) {
      toast.error("네트워크 오류가 발생했습니다.");
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch (e) {}
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-stone-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="restore-listing-modal"
        >
          <motion.div
            initial={{ scale: 0.94, opacity: 0, y: 8 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden"
          >
            <button
              onClick={handleDismiss}
              aria-label="닫기"
              className="absolute top-4 right-4 w-9 h-9 rounded-full hover:bg-stone-100 flex items-center justify-center text-stone-500"
              data-testid="restore-listing-close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="px-7 pt-9 pb-2 text-center">
              <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-100 to-rose-100 flex items-center justify-center mb-4">
                <EyeOff className="w-7 h-7 text-amber-700" />
              </div>
              <h2 className="text-xl font-bold text-stone-900 leading-snug">
                회원정보보기 목록에<br />
                <span className="text-burgundy-600">다시 노출</span>하시겠어요?
              </h2>
              <p className="mt-3 text-sm text-stone-600 leading-relaxed">
                현재 회원님의 프로필은 다른 회원들에게 보이지 않도록
                <br />
                <strong className="text-stone-800">숨김 상태</strong>로 설정되어 있어요.
                <br />
                다시 인연을 만나보고 싶으시면 노출을 켜주세요.
              </p>
            </div>

            <div className="px-7 py-5 grid grid-cols-2 gap-3">
              <button
                onClick={handleDismiss}
                disabled={busy}
                className="px-4 py-3 rounded-xl border border-stone-200 text-stone-700 font-medium hover:bg-stone-50 transition-colors disabled:opacity-50"
                data-testid="restore-listing-keep-hidden"
              >
                지금은 유지
              </button>
              <button
                onClick={handleRestore}
                disabled={busy}
                className="px-4 py-3 rounded-xl bg-burgundy-600 text-white font-semibold hover:bg-burgundy-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="restore-listing-confirm"
              >
                {busy ? (
                  <span>처리 중…</span>
                ) : (
                  <>
                    <Eye className="w-4 h-4" />
                    다시 노출
                  </>
                )}
              </button>
            </div>

            <div className="px-7 pb-6 text-[11px] text-stone-400 text-center flex items-center justify-center gap-1">
              <Sparkles className="w-3 h-3" />
              언제든 마이페이지에서 다시 변경할 수 있어요
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
